// src/modules/comission/hooks/useExportComissionPDF.ts
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../../assets/logo_report.png";
import { globalMaskDocument, globalTranslateTypeComission, globalTranslateStatusComission, globalFormatCurrencyBRL } from "../../../shared/utils";
import { format } from "date-fns";

export const useExportComissionPDF = (setIsLoading: (v: boolean) => void) => {
	const exportComissionToPDF = async (items: any[], clientName: string, dataIni: string, dataFim: string) => {
		try {
			setIsLoading(true);

			const doc = new jsPDF({
				orientation: "landscape",
				unit: "pt",
				format: "a4",
			});

			// Logo
			const logoX = 40;
			const logoY = 20;
			const logoWidth = 120;
			const logoHeight = 30; // menor altura
			doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

			// "Solicitado em" no canto direito
			const now = new Date();
			const solicitDate = format(now, "dd/MM/yyyy - HH:mm");
			doc.setFontSize(9);
			doc.text(`Solicitado em: ${solicitDate}`, 770, 35, { align: "right" });

			// Cabeçalho bloco
			let lastY = logoY + logoHeight + 20;
			doc.setFontSize(12);
			doc.setFont("helvetica", "bold");
			doc.text(clientName, logoX, lastY);
			lastY += 15;

			doc.setFontSize(11);
			doc.setFont("helvetica", "normal");
			doc.text(
				`Período: ${format(new Date(dataIni + " 03:00:00"), "dd/MM/yyyy")} até ${format(new Date(dataFim + " 03:00:00"), "dd/MM/yyyy")}`,
				logoX,
				lastY
			);
			lastY += 15;

			doc.text(`Total de linhas: ${items.length}`, logoX, lastY);
			lastY += 15;

			// Linha divisória suave
			doc.setDrawColor(224); // cor #E0E0E0
			doc.setLineWidth(0.5);
			doc.line(logoX, lastY, 800, lastY);
			lastY += 20;

			// Resumo da Premiação - estilo saldo elegante (tudo em uma linha só)
			doc.setFontSize(10);
			doc.setFont("helvetica", "normal");
			doc.text("Resumo da Premiação", logoX, lastY);
			lastY += 15;

			// 1️⃣ Filtrando itens válidos (não pagos/suspensos/cancelados)
			const validItems = items.filter((i) => !["paid", "suspended", "canceled"].includes(i.status));

			// 2️⃣ Resumindo por tipo
			const resumo = ["prize", "bonus", "discount"].map((type) => {
				const label = type === "prize" ? "Premiação" : type === "bonus" ? "Bonificação" : "Estorno";
				const filtered = validItems.filter((i) => i.type === type);
				const total = filtered.reduce((acc, i) => acc + Number(i.value), 0);
				return {
					label,
					totalValue: total,
					totalFormatted: globalFormatCurrencyBRL((total ?? 0).toString()),
				};
			});

			// 3️⃣ Cálculo do saldo a pagar (líquido)
			const totalPremiacao = resumo.find((r) => r.label === "Premiação")?.totalValue ?? 0;
			const totalBonificacao = resumo.find((r) => r.label === "Bonificação")?.totalValue ?? 0;
			const totalEstorno = resumo.find((r) => r.label === "Estorno")?.totalValue ?? 0;

			const saldoAPagar = totalPremiacao + totalBonificacao - totalEstorno;

			// 4️⃣ Resumo pagos
			const totalPagos = items.filter((i) => i.status === "paid").reduce((acc, i) => acc + Number(i.value), 0);

			// 5️⃣ Resumo cancelados/suspensos
			const totalCanceladosSuspensos = items.filter((i) => ["suspended", "canceled"].includes(i.status)).reduce((acc, i) => acc + Number(i.value), 0);

			// Layout das colunas em uma única linha
			const col1X = logoX;
			const col2X = col1X + 130;
			const col3X = col2X + 130;
			const col4X = col3X + 130;
			const col5X = col4X + 130;
			const col6X = col5X + 130;

			// Labels (1 linha)
			doc.setFontSize(9);
			doc.setFont("helvetica", "normal");
			doc.text(resumo[0].label, col1X, lastY);
			doc.text(resumo[1].label, col2X, lastY);
			doc.text(resumo[2].label, col3X, lastY);
			doc.text("Saldo a Pagar (líquido)", col4X, lastY);
			doc.text("Total Pagos", col5X, lastY);
			doc.text("Total Cancelados / Suspensos", col6X, lastY);
			lastY += 12;

			// Valores (1 linha)
			doc.setFontSize(11);
			doc.setFont("helvetica", "bold");
			doc.text(resumo[0].totalFormatted, col1X, lastY);
			doc.text(resumo[1].totalFormatted, col2X, lastY);
			doc.text(resumo[2].totalFormatted, col3X, lastY);
			doc.text(globalFormatCurrencyBRL(saldoAPagar.toString()), col4X, lastY);
			doc.text(globalFormatCurrencyBRL(totalPagos.toString()), col5X, lastY);
			doc.text(globalFormatCurrencyBRL(totalCanceladosSuspensos.toString()), col6X, lastY);
			lastY += 20;

			// Linha divisória suave
			doc.setDrawColor(224); // cor #E0E0E0
			doc.setLineWidth(0.5);
			doc.line(logoX, lastY, 800, lastY);
			lastY += 20;

			// Título Extrato das Bonificações/Premiações - refinado
			doc.setFontSize(12); // menor e elegante
			doc.setFont("helvetica", "bold");
			doc.text("Extrato das Bonificações/Premiações", logoX, lastY);
			lastY += 10;

			autoTable(doc, {
				startY: lastY,
				head: [["Certificado", "Nome", "Documento", "Tipo", "Parcela", "Valor Parcela", "Índice", "Valor Premio", "Vencimento", "Status"]],
				body: items.map((item) => [
					item.proposalCertificateNumber ?? "-",
					item.proposalName ?? "Cliente sem nome",
					globalMaskDocument(item.proposalDocument),
					globalTranslateTypeComission(item.type),
					item.installmentNumber ? `${item.installmentNumber} / ${item.proposalRentMonthsCount}` : "-",
					globalFormatCurrencyBRL((item.installmentValue ?? 0).toString()),
					item.proposalCommissionValue ? `${Number(item.proposalCommissionValue).toFixed(2)}%` : "",
					globalFormatCurrencyBRL((item.value ?? 0).toString()),
					item.dueDate ? format(new Date(item.dueDate), "dd/MM/yyyy") : "-",
					globalTranslateStatusComission(item.status),
				]),
				styles: { font: "helvetica", fontSize: 9 },
				headStyles: { fillColor: [79, 129, 189], textColor: 255 },
				alternateRowStyles: { fillColor: [245, 245, 245] }, // cinza suave nas linhas alternadas

				// AQUI é onde você personaliza o Status
				didParseCell: function (data) {
					// Só aplicar no campo "Status", que é a última coluna (index 9)
					if (data.section === "body" && data.column.index === 9) {
						const statusText = data.cell.text[0]?.toLowerCase() ?? "";

						if (statusText.includes("pago")) {
							// Verde
							data.cell.styles.textColor = [34, 139, 34]; // verde escuro (forestgreen)
						} else if (statusText.includes("cancelado") || statusText.includes("suspenso")) {
							// Vermelho
							data.cell.styles.textColor = [220, 20, 60]; // vermelho (crimson)
						} else {
							// Default preto
							data.cell.styles.textColor = [0, 0, 0];
						}
					}
				},
			});

			// PAGINAÇÃO correta no rodapé (após autoTable)
			const pageCount = doc.getNumberOfPages();
			for (let i = 1; i <= pageCount; i++) {
				doc.setPage(i);
				doc.setFontSize(9);
				doc.setTextColor(150);
				doc.text(`Página ${i} de ${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 20, { align: "center" });
			}

			// Salvar PDF
			doc.save(`relatorio-comissoes-${clientName}.pdf`);
		} catch (err) {
			console.error("Erro ao exportar PDF:", err);
		} finally {
			setIsLoading(false);
		}
	};

	return { exportComissionToPDF };
};
